import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode
} from 'react';
import { createPortal } from 'react-dom';
import { useFixedAnchoredPanel } from './useFixedAnchoredPanel';

/**
 * Typeahead pick-or-create dropdown. Mounted in 14.3d as the `single_select`
 * custom-attribute control on the document editor inspector. Renders an
 * `<input>` filter over the supplied option pool (typically
 * `optionsByAttrDef.get(defId)` from `useDocumentEditorVM`); on commit the
 * picker collapses into a chip showing the chosen label with a × clear
 * affordance.
 *
 * If the typed query has no exact-label match (case-insensitive), the
 * popover surfaces a "+ Create '<query>'" footer. Choosing it (Enter while
 * highlighted or mousedown click) invokes `onCreate(label)` which the parent
 * VM wires to the `create_custom_attribute_option` RPC; on resolve the
 * picker commits the newly-minted option as the value via `onPick`.
 *
 * Value shape: `{ option_id: string } | null` (consistent with the
 * `custom_attribute_value_versions.value jsonb` schema). The picker is
 * value-agnostic beyond resolving the matching `PickOrCreateOption` from
 * `options` — callers own the persisted shape.
 */

export interface PickOrCreateOption {
  id: string;
  label: string;
}

export interface PickOrCreateProps {
  id: string;
  label: string;
  options: PickOrCreateOption[];
  value: { option_id: string } | null;
  onPick: (next: PickOrCreateOption | null) => void;
  /** Mint a new option with `label`; return the new option so the picker
   *  can commit it, or `null` on failure. */
  onCreate: (label: string) => Promise<PickOrCreateOption | null>;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** `pill` — property-bar chip opens the list directly (no nested popover). */
  triggerVariant?: 'default' | 'pill';
  /** Formatted `"Label: value"` text when `triggerVariant="pill"`. */
  pillLabel?: string;
  pillMarker?: ReactNode;
  pillMarkerTestId?: string;
  pillMuted?: boolean;
  'data-testid'?: string;
}

export function PickOrCreate({
  id,
  label,
  options,
  value,
  onPick,
  onCreate,
  disabled,
  placeholder = 'Pick or create…',
  className,
  triggerVariant = 'default',
  pillLabel,
  pillMarker,
  pillMarkerTestId,
  pillMuted,
  ...rest
}: PickOrCreateProps) {
  const testId = rest['data-testid'];
  const pillRef = useRef<HTMLButtonElement>(null);
  const selected = value
    ? options.find((o) => o.id === value.option_id) ?? null
    : null;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [creating, setCreating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const needle = query.trim().toLowerCase();
  const filtered = needle
    ? options.filter((o) => o.label.toLowerCase().includes(needle))
    : options;
  const trimmed = query.trim();
  const showCreate =
    trimmed.length > 0 &&
    !options.some((o) => o.label.toLowerCase() === trimmed.toLowerCase());
  const createIndex = filtered.length;
  const itemCount = filtered.length + (showCreate ? 1 : 0);

  const closePanel = () => {
    setOpen(false);
    setQuery('');
    setHighlighted(0);
  };

  const { panelRef, style: panelStyle } = useFixedAnchoredPanel(
    pillRef,
    open && triggerVariant === 'pill',
    closePanel,
    'start'
  );

  useEffect(() => {
    if (!open || triggerVariant === 'pill') return;
    const onDocPointer = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocPointer);
    return () => document.removeEventListener('mousedown', onDocPointer);
  }, [open, triggerVariant]);

  const commit = (opt: PickOrCreateOption) => {
    onPick(opt);
    setQuery('');
    setHighlighted(0);
    setOpen(false);
  };

  const doCreate = async () => {
    if (!trimmed || creating) return;
    setCreating(true);
    const created = await onCreate(trimmed);
    setCreating(false);
    if (created) {
      commit(created);
    }
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, Math.max(itemCount - 1, 0)));
      setOpen(true);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (highlighted === createIndex && showCreate) {
        e.preventDefault();
        void doCreate();
      } else {
        const pick = filtered[highlighted];
        if (pick) {
          e.preventDefault();
          commit(pick);
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closePanel();
    }
  };

  const renderList = () => (
    <>
      {filtered.map((opt, idx) => (
        <li
          key={opt.id}
          role="option"
          aria-selected={idx === highlighted}
          className={[
            'pn-pickorcreate-option',
            idx === highlighted ? 'pn-pickorcreate-option-active' : null
          ]
            .filter(Boolean)
            .join(' ')}
          onMouseDown={(e) => {
            e.preventDefault();
            commit(opt);
          }}
          onMouseEnter={() => setHighlighted(idx)}
          data-testid={testId ? `${testId}-option-${opt.id}` : undefined}
        >
          <span className="pn-pickorcreate-option-label">{opt.label}</span>
        </li>
      ))}
      {showCreate ? (
        <li
          key="__create__"
          role="option"
          aria-selected={createIndex === highlighted}
          className={[
            'pn-pickorcreate-option',
            'pn-pickorcreate-create',
            createIndex === highlighted ? 'pn-pickorcreate-option-active' : null
          ]
            .filter(Boolean)
            .join(' ')}
          onMouseDown={(e) => {
            e.preventDefault();
            void doCreate();
          }}
          onMouseEnter={() => setHighlighted(createIndex)}
          data-testid={testId ? `${testId}-create` : undefined}
          aria-busy={creating || undefined}
        >
          <span className="pn-pickorcreate-create-prefix">+ Create</span>
          <span className="pn-pickorcreate-create-label">“{trimmed}”</span>
        </li>
      ) : null}
    </>
  );

  if (triggerVariant === 'pill') {
    const pillCls = [
      'pn-property-pill',
      pillMuted ? 'pn-property-pill-muted' : null
    ]
      .filter(Boolean)
      .join(' ');
    const openPicker = () => {
      if (disabled) return;
      setQuery('');
      setHighlighted(0);
      setOpen(true);
    };
    const panel = open ? (
      <div
        ref={panelRef}
        className="pn-pickorcreate-panel pn-popover-fixed"
        style={panelStyle}
        role="dialog"
        aria-label={label}
        data-testid={testId ? `${testId}-panel` : undefined}
      >
        <input
          type="text"
          className="pn-field-input pn-pickorcreate-input"
          value={query}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlighted(0);
          }}
          onKeyDown={onKeyDown}
          autoComplete="off"
          role="combobox"
          aria-expanded
          aria-controls={`${id}-listbox`}
          data-testid={testId ? `${testId}-input` : undefined}
        />
        <ul
          id={`${id}-listbox`}
          className="pn-pickorcreate-list pn-pickorcreate-list-portal"
          role="listbox"
        >
          {filtered.length > 0 || showCreate ? (
            renderList()
          ) : (
            <li className="pn-pickorcreate-empty" aria-disabled="true">
              No options
            </li>
          )}
        </ul>
      </div>
    ) : null;

    return (
      <>
        <button
          ref={pillRef}
          type="button"
          className={pillCls}
          disabled={disabled}
          onClick={openPicker}
          aria-haspopup="listbox"
          aria-expanded={open}
          data-testid={testId}
        >
          {pillLabel}
          {pillMarker ? (
            <>
              {' '}
              <span
                className="pn-property-pill-marker"
                data-testid={pillMarkerTestId}
                aria-hidden="true"
              >
                {pillMarker}
              </span>
            </>
          ) : null}
        </button>
        {panel ? createPortal(panel, document.body) : null}
      </>
    );
  }

  // Collapsed (selected) state — chip + × clear.
  if (selected && !open) {
    const wrapperClass = [
      'pn-pickorcreate',
      'pn-pickorcreate-collapsed',
      className
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <div
        ref={containerRef}
        className={wrapperClass}
        data-testid={testId}
        data-state="collapsed"
      >
        <label htmlFor={id} className="pn-field-label pn-field-label-hidden">
          {label}
        </label>
        <button
          type="button"
          id={id}
          className="pn-pickorcreate-chip"
          disabled={disabled}
          onClick={() => {
            setQuery('');
            setHighlighted(0);
            setOpen(true);
          }}
          data-testid={testId ? `${testId}-chip` : undefined}
        >
          <span className="pn-pickorcreate-chip-label">{selected.label}</span>
        </button>
        <button
          type="button"
          className="pn-pickorcreate-clear"
          aria-label={`Clear ${label}`}
          disabled={disabled}
          onClick={() => onPick(null)}
          data-testid={testId ? `${testId}-clear` : undefined}
        >
          ×
        </button>
      </div>
    );
  }

  const wrapperClass = ['pn-pickorcreate', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={containerRef}
      className={wrapperClass}
      data-testid={testId}
      data-state="open"
    >
      <label htmlFor={id} className="pn-field-label pn-field-label-hidden">
        {label}
      </label>
      <input
        type="text"
        id={id}
        className="pn-field-input pn-pickorcreate-input"
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          setQuery(e.target.value);
          setHighlighted(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        data-testid={testId ? `${testId}-input` : undefined}
      />
      {open && (filtered.length > 0 || showCreate) ? (
        <ul
          id={`${id}-listbox`}
          className="pn-pickorcreate-list"
          role="listbox"
          data-testid={testId ? `${testId}-list` : undefined}
        >
          {filtered.map((opt, idx) => (
            <li
              key={opt.id}
              role="option"
              aria-selected={idx === highlighted}
              className={[
                'pn-pickorcreate-option',
                idx === highlighted ? 'pn-pickorcreate-option-active' : null
              ]
                .filter(Boolean)
                .join(' ')}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(opt);
              }}
              onMouseEnter={() => setHighlighted(idx)}
              data-testid={
                testId ? `${testId}-option-${opt.id}` : undefined
              }
            >
              <span className="pn-pickorcreate-option-label">{opt.label}</span>
            </li>
          ))}
          {showCreate ? (
            <li
              key="__create__"
              role="option"
              aria-selected={createIndex === highlighted}
              className={[
                'pn-pickorcreate-option',
                'pn-pickorcreate-create',
                createIndex === highlighted
                  ? 'pn-pickorcreate-option-active'
                  : null
              ]
                .filter(Boolean)
                .join(' ')}
              onMouseDown={(e) => {
                e.preventDefault();
                void doCreate();
              }}
              onMouseEnter={() => setHighlighted(createIndex)}
              data-testid={testId ? `${testId}-create` : undefined}
              aria-busy={creating || undefined}
            >
              <span className="pn-pickorcreate-create-prefix">+ Create</span>
              <span className="pn-pickorcreate-create-label">
                “{trimmed}”
              </span>
            </li>
          ) : null}
        </ul>
      ) : open ? (
        <ul
          id={`${id}-listbox`}
          className="pn-pickorcreate-list pn-pickorcreate-list-empty"
          role="listbox"
          data-testid={testId ? `${testId}-list` : undefined}
        >
          <li className="pn-pickorcreate-empty" aria-disabled="true">
            No options
          </li>
        </ul>
      ) : null}
    </div>
  );
}
